/**
 * VariantPicker — surfaces sibling course flavours (3yr BSc, 4yr MSci,
 * sandwich, year abroad, foundation) as a compact chip strip above
 * the degree hero. Powered by `GET /api/degrees/{id}/variants` which
 * de-dupes HESA near-duplicate rows down to the real flavour set.
 *
 * Hidden entirely when the current course has no siblings — so we
 * never render a chooser with one lone option.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

export default function VariantPicker({ degreeId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/degrees/${degreeId}/variants`);
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData({ count: 0, variants: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [degreeId]);

  if (!data || !data.count || data.count <= 1) return null;

  return (
    <div
      className="mt-6 mb-5 rounded-lg border border-[#4C1D95]/30 bg-[#F5F1EA]/50 p-4"
      data-testid="variant-picker"
    >
      <div className="flex items-baseline gap-2 mb-3 flex-wrap">
        <span className="text-[11px] font-underground-sc tracking-[0.15em] text-[#4C1D95]">
          Course flavours
        </span>
        <span className="text-xs text-stone-600">
          Same subject at this university — {data.count} routes available
        </span>
      </div>
      <div className="flex flex-wrap gap-2" role="tablist">
        {data.variants.map((v) => {
          const active = v.id === data.current_id;
          return (
            <Link
              key={v.id}
              to={`/degree/${v.id}`}
              data-testid={`variant-chip-${v.id}`}
              data-active={active ? "true" : "false"}
              role="tab"
              aria-selected={active}
              className={
                "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors " +
                (active
                  ? "border-[#4C1D95] bg-[#4C1D95] text-white cursor-default"
                  : "border-stone-300 bg-white text-stone-800 hover:border-[#4C1D95] hover:text-[#4C1D95]")
              }
              onClick={(e) => {
                if (active) e.preventDefault();
              }}
            >
              <span className="font-mono text-[10px] uppercase tracking-wider opacity-70">
                {v.qualification || "BA/BSc"}
              </span>
              <span className="font-medium">
                {v.duration_years ? `${v.duration_years}yr` : "—"}
              </span>
              {v.foundation && <span className="text-[10px] opacity-80">+ Foundation</span>}
              {v.sandwich && <span className="text-[10px] opacity-80">+ Sandwich</span>}
              {v.year_abroad && <span className="text-[10px] opacity-80">+ Year abroad</span>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
